import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractUsPhoneDigits, formatUsPhoneInput } from '../../utils/usPhone';
import {
  prospectStatusBadgeClass,
  prospectStatusLabel,
  resolveProspectStatuses,
} from '../../utils/prospectStatus';
import UsAddressAutocomplete from '../ui/UsAddressAutocomplete';
import ProspectLevelStars from '../ui/ProspectLevelStars';
import Select from '../ui/Select';
import { inputClassWithError } from '../ui/formStyles';
import { formatNoteDate, getProspectNotesEntries } from '../../utils/prospectNotes';
import {
  segmentButtonClass,
  YesNoField,
  HousingField,
} from './CreateProspectWizard';

const VALIDATED_FIELDS = ['name', 'email', 'phone'];

function validateField(key, form, p, t) {
  switch (key) {
    case 'name':
      if (!form.name.trim()) return p.nameRequired || 'Name is required';
      return '';
    case 'email': {
      const email = form.email.trim();
      if (email && !email.includes('@')) return p.emailInvalid || 'Enter a valid email address';
      return '';
    }
    case 'phone': {
      const digits = extractUsPhoneDigits(form.phone);
      if (digits && digits.length !== 10) {
        return p.phoneInvalid || t.referral?.phoneInvalid || 'Enter a valid 10-digit US phone number';
      }
      return '';
    }
    default:
      return '';
  }
}

function prospectToForm(prospect) {
  return {
    name: prospect.name || '',
    email: prospect.email || '',
    phone: prospect.phone ? formatUsPhoneInput(prospect.phone) : '',
    address: prospect.address || '',
    apartment: prospect.apartment || '',
    status: prospect.status || 'NEW',
    prospection_level: prospect.prospection_level || 0,
    prospect_type: String(prospect.prospect_type || 'client').toLowerCase() === 'partner' ? 'partner' : 'client',
    is_employed: prospect.is_employed ?? null,
    is_married: prospect.is_married ?? null,
    housing_type: prospect.housing_type || null,
  };
}

function ProspectStatusSelect({ value, onChange, t, prospectStatuses, disabled, className = '' }) {
  return (
    <Select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      triggerClassName={`font-medium ${className}`}
      className="w-full rounded-xl border border-secondary-300/70 bg-white/90 px-3 py-2.5 text-sm shadow-soft focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30 disabled:cursor-not-allowed disabled:opacity-60   "
      aria-label={prospectStatusLabel(value, t, prospectStatuses)}
    >
      {prospectStatuses.map((status) => (
        <option key={status.code} value={status.code}>
          {prospectStatusLabel(status.code, t, prospectStatuses)}
        </option>
      ))}
    </Select>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-xs text-red-600 " role="alert">
      {message}
    </p>
  );
}

function ProspectEditModal({
  prospect,
  isOpen,
  onClose,
  onSave,
  onAddNote,
  readOnly = false,
  prospectStatuses = [],
  partnerStatuses = [],
  defaultStatus = 'NEW',
  partnerDefaultStatus = 'NEW',
}) {
  const { t, locale } = useLanguage();
  const p = t.vendor?.prospects || {};
  const [form, setForm] = useState(() => prospectToForm(prospect || {}));
  const [touched, setTouched] = useState({ name: false, email: false, phone: false });
  const [errors, setErrors] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    if (prospect && isOpen) {
      setForm(prospectToForm(prospect));
      setTouched({ name: false, email: false, phone: false });
      setErrors({ name: '', email: '', phone: '' });
      setNewNote('');
    }
  }, [prospect, isOpen]);

  const notesEntries = useMemo(
    () =>
      prospect
        ? [...getProspectNotesEntries(prospect)].sort((a, b) =>
            String(b.date).localeCompare(String(a.date))
          )
        : [],
    [prospect]
  );

  const statusOptions = useMemo(
    () => resolveProspectStatuses(form.prospect_type, prospectStatuses, partnerStatuses),
    [form.prospect_type, prospectStatuses, partnerStatuses]
  );

  function toggleProspectType(nextType) {
    if (readOnly || nextType === form.prospect_type) return;
    setForm((prev) => ({
      ...prev,
      prospect_type: nextType,
      status:
        nextType === 'partner'
          ? partnerDefaultStatus
          : defaultStatus,
    }));
  }

  if (!prospect) return null;

  function fieldErrorClass(field) {
    return !!(touched[field] && errors[field]);
  }

  const inputClass = (field) => inputClassWithError(fieldErrorClass(field));

  function validateAndSetField(key, nextForm) {
    const message = validateField(key, nextForm, p, t);
    setErrors((prev) => ({ ...prev, [key]: message }));
    return message;
  }

  function handleBlur(key) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    validateAndSetField(key, form);
  }

  function updateField(key, value) {
    setForm((prev) => {
      const nextForm = { ...prev, [key]: value };
      if (touched[key]) validateAndSetField(key, nextForm);
      return nextForm;
    });
  }

  const canSave = VALIDATED_FIELDS.every((key) => !validateField(key, form, p, t));

  async function handleSave(e) {
    e.preventDefault();
    if (!canSave || readOnly) return;
    setSaving(true);
    try {
      await onSave(prospect.id, {
        name: form.name.trim(),
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        apartment: form.apartment || null,
        status: form.status,
        prospect_type: form.prospect_type,
        ...(form.prospect_type === 'partner'
          ? { is_employed: null, is_married: null, housing_type: null }
          : {
              is_employed: form.is_employed,
              is_married: form.is_married,
              housing_type: form.housing_type || null,
              prospection_level: form.prospection_level || null,
            }),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  async function handleAddNote(e) {
    e.preventDefault();
    const trimmed = newNote.trim();
    if (!trimmed || readOnly) return;
    setAddingNote(true);
    try {
      await onAddNote(prospect.id, trimmed);
      setNewNote('');
    } finally {
      setAddingNote(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={p.editProspectTitle || 'Edit prospect'}
      size="large"
    >
      <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
        <p className="text-sm text-secondary-600 ">
          {p.editModalDesc || 'Update prospect details and manage notes.'}
        </p>

        <div className="space-y-1.5">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.typeLabel || 'Prospect type'}
          </span>
          <div
            className="inline-flex w-full rounded-xl border border-secondary-200/80 bg-white/80 p-1 shadow-soft  "
            role="group"
            aria-label={p.typeLabel || 'Prospect type'}
          >
            <button
              type="button"
              className={segmentButtonClass(form.prospect_type === 'client')}
              aria-pressed={form.prospect_type === 'client'}
              disabled={readOnly}
              onClick={() => toggleProspectType('client')}
            >
              {p.typeClient || 'Client'}
            </button>
            <button
              type="button"
              className={segmentButtonClass(form.prospect_type === 'partner')}
              aria-pressed={form.prospect_type === 'partner'}
              disabled={readOnly}
              onClick={() => toggleProspectType('partner')}
            >
              {p.typePartner || 'Partner'}
            </button>
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.nameLabel || 'Full name or business *'}
          </span>
          <input
            className={inputClass('name')}
            autoFocus
            placeholder={p.namePlaceholder || 'e.g. Jane Smith or Acme Water Co.'}
            value={form.name}
            disabled={readOnly}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            aria-invalid={touched.name && Boolean(errors.name)}
            aria-describedby={errors.name ? 'edit-prospect-name-error' : undefined}
          />
          <FieldError id="edit-prospect-name-error" message={touched.name ? errors.name : ''} />
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-secondary-700 ">
              {p.emailLabel || 'Email'}
            </span>
            <input
              type="email"
              autoComplete="email"
              className={inputClass('email')}
              placeholder={p.emailPlaceholder || 'name@company.com'}
              value={form.email}
              disabled={readOnly}
              onChange={(e) => updateField('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              aria-invalid={touched.email && Boolean(errors.email)}
              aria-describedby={errors.email ? 'edit-prospect-email-error' : undefined}
            />
            <FieldError id="edit-prospect-email-error" message={touched.email ? errors.email : ''} />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-secondary-700 ">
              {p.phoneLabel || 'Phone'}
            </span>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              className={inputClass('phone')}
              placeholder="(555) 555-0123"
              maxLength={14}
              value={form.phone}
              disabled={readOnly}
              onChange={(e) => updateField('phone', formatUsPhoneInput(e.target.value))}
              onBlur={() => handleBlur('phone')}
              aria-invalid={touched.phone && Boolean(errors.phone)}
              aria-describedby={errors.phone ? 'edit-prospect-phone-error' : undefined}
            />
            <FieldError id="edit-prospect-phone-error" message={touched.phone ? errors.phone : ''} />
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.addressLabel || 'Address'}
          </span>
          <UsAddressAutocomplete
            className={`${inputClass('address')} ${readOnly ? 'pointer-events-none opacity-60' : ''}`}
            placeholder={p.addressPlaceholder || '123 Main St, City, ST 12345 (optional)'}
            searchingLabel={p.addressSearching || 'Searching addresses…'}
            value={form.address}
            onChange={(address) => !readOnly && setForm((f) => ({ ...f, address }))}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.apartmentLabel || 'Apartment'}
          </span>
          <input
            className={inputClass('apartment')}
            placeholder={p.apartmentPlaceholder || 'Apt, suite, unit… (optional)'}
            value={form.apartment}
            disabled={readOnly}
            onChange={(e) => setForm((f) => ({ ...f, apartment: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.statusLabel || 'Prospect status'}
          </span>
          <div className="mt-1.5 flex items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${prospectStatusBadgeClass(form.status, statusOptions)}`}
            >
              {prospectStatusLabel(form.status, t, statusOptions)}
            </span>
          </div>
          <ProspectStatusSelect
            value={form.status}
            onChange={(status) => setForm((f) => ({ ...f, status }))}
            disabled={readOnly}
            t={t}
            prospectStatuses={statusOptions}
            className="mt-1.5"
          />
        </label>

        {form.prospect_type !== 'partner' && (
          <fieldset className="rounded-xl border border-secondary-200/70 bg-secondary-50/50 p-4 lg:p-3  ">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-secondary-600 ">
              {p.creationProfileTitle || 'Prospect creation'}
            </legend>
            <p className="mt-1 px-1 text-xs text-secondary-500 ">
              {p.creationProfileOptional || 'Optional — leave unanswered if unknown.'}
            </p>
            <div className="mt-3 space-y-3 lg:mt-2 lg:space-y-2.5">
              <YesNoField
                label={p.worksLabel || 'Works?'}
                value={form.is_employed}
                onChange={(next) => setForm((f) => ({ ...f, is_employed: next }))}
                yesLabel={t.common?.yes || 'Yes'}
                noLabel={t.common?.no || 'No'}
              />
              <YesNoField
                label={p.marriedLabel || 'Married?'}
                value={form.is_married}
                onChange={(next) => setForm((f) => ({ ...f, is_married: next }))}
                yesLabel={t.common?.yes || 'Yes'}
                noLabel={t.common?.no || 'No'}
              />
              <HousingField
                label={p.housingLabel || 'Own home or rent?'}
                value={form.housing_type}
                onChange={(next) => setForm((f) => ({ ...f, housing_type: next }))}
                ownLabel={p.ownsHomeLabel || 'Own home'}
                rentLabel={p.rentsHomeLabel || 'Rent'}
              />
            </div>
          </fieldset>
        )}

        {form.prospect_type !== 'partner' && (
          <label className="block">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.prospectionLevelLabel || 'Prospection level'}
          </span>
          <p className="mt-0.5 text-xs text-secondary-500 ">
            {p.prospectionLevelHint || 'Optional — rate the quality of this prospect from 1 to 5 stars.'}
          </p>
          <div className="mt-2 flex justify-center py-2">
            <ProspectLevelStars
              value={form.prospection_level}
              onChange={
                readOnly
                  ? undefined
                  : (level) => setForm((f) => ({ ...f, prospection_level: level }))
              }
              readOnly={readOnly}
              size="lg"
              centered
              ariaLabel={p.prospectionLevelLabel || 'Prospection level'}
            />
          </div>
        </label>
        )}

        <div className="border-t border-secondary-200/60 pt-4 ">
          <h4 className="text-xs font-medium uppercase tracking-wide text-secondary-500 ">
            {p.notesHistory || 'Notes history'}
          </h4>
          {notesEntries.length === 0 ? (
            <p className="mt-3 rounded-xl border border-dashed border-secondary-200/70 bg-secondary-50/50 p-4 text-sm text-secondary-500   ">
              {p.noNotes || 'No notes added'}
            </p>
          ) : (
            <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
              {notesEntries.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className="rounded-xl border border-secondary-200/70 bg-secondary-50/80 p-3  "
                >
                  <p className="text-xs font-semibold text-orange-600 ">
                    {formatNoteDate(entry.date, locale)}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-secondary-800 ">
                    {entry.text}
                  </p>
                </div>
              ))}
            </div>
          )}

          {!readOnly ? (
            <label className="mt-4 block">
              <span className="text-xs font-medium text-secondary-700 ">
                {p.addNote || 'Add note'}
              </span>
              <textarea
                className={`${inputClass('notes')} min-h-[88px] resize-y`}
                placeholder={p.addNotePlaceholder || 'Write a new note for this prospect…'}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  size="small"
                  variant="secondary"
                  loading={addingNote}
                  disabled={!newNote.trim() || addingNote}
                  onClick={(e) => void handleAddNote(e)}
                >
                  {p.addNote || 'Add note'}
                </Button>
              </div>
            </label>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-secondary-200/60 pt-4 sm:flex-row sm:justify-end ">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            {t.common?.cancel || 'Cancel'}
          </Button>
          {!readOnly ? (
            <Button type="submit" loading={saving} disabled={!canSave || saving}>
              {p.saveChanges || 'Save changes'}
            </Button>
          ) : (
            <Button type="button" variant="secondary" onClick={onClose}>
              {p.close || t.common?.close || 'Close'}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ProspectEditModal;
